import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { listPostmarkIdentities } from './api';
import {
  NEWSLETTER_SENDER_FROM,
  NEWSLETTER_SENDER_REPLY_TO,
} from '../../lib/newsletterSender';
import PostmarkSignatureModal from './PostmarkSignatureModal';
import { SenderEnvelope } from './SenderEnvelope';
import type { PostmarkIdentity } from './types';

/**
 * Same data as PostmarkSignatureSelect — the resolved From and Reply-To — but read-only
 * in the form, with the list behind a button. Choosing a sender is rare next to editing
 * a name or a status, so it does not need a form control sitting open all the time.
 *
 * The select version stays for the newsletter settings tab and the create modal; this
 * is the condensed one.
 */
export interface PostmarkSignaturePickerProps {
  value: number | null;
  onChange: (signatureId: number | null) => void;
  disabled?: boolean;
  /** Sentence case by default, to match neighbouring field labels. */
  label?: string;
}

export default function PostmarkSignaturePicker({
  value,
  onChange,
  disabled = false,
  label = 'Postmark signature',
}: PostmarkSignaturePickerProps) {
  const [signatures, setSignatures] = useState<PostmarkIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listPostmarkIdentities();
      setSignatures(result.identities.filter((row) => row.kind === 'signature'));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load signatures');
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () => signatures.find((s) => s.id === value) ?? null,
    [signatures, value],
  );

  const summaryName = selected
    ? selected.name || selected.emailAddress || `Signature ${selected.id}`
    : 'Default (engine)';

  return (
    <div className="min-w-0">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>

      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
              Loading…
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {summaryName}
              </p>
              <SenderEnvelope
                from={selected ? selected.fromLine : NEWSLETTER_SENDER_FROM}
                replyTo={
                  selected
                    ? selected.replyToEmailAddress ?? 'not set'
                    : NEWSLETTER_SENDER_REPLY_TO
                }
              />
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled || loading}
          className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
          title="Change Postmark signature"
        >
          <Pencil className="w-3.5 h-3.5" />
          Change
        </button>
      </div>

      {error ? (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Resolved from Postmark at send time.
        </p>
      )}

      <PostmarkSignatureModal
        isOpen={open}
        value={value}
        signatures={signatures}
        onClose={() => setOpen(false)}
        onSelect={onChange}
      />
    </div>
  );
}
